import React, { useEffect, useState } from 'react';
import { adminListBlog, adminCreateBlog, adminUpdateBlog, adminDeleteBlog, adminGenerateBlog } from '../../lib/api';
import axios from 'axios';
import { API_BASE } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { useToast } from '../../hooks/use-toast';
import { Loader2, Plus, Pencil, Trash2, Eye, Sparkles } from 'lucide-react';

const EMPTY = { title: '', excerpt: '', content: '', cover_image: '', tags: '', author: 'Hipro Editorial Team', status: 'published' };

export default function AdminBlogTab() {
  const { toast } = useToast();
  const [posts, setPosts] = useState(null);
  const [editing, setEditing] = useState(null); // null | {id?} form state
  const [busy, setBusy] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const [topic, setTopic] = useState('');
  const [generating, setGenerating] = useState(false);

  const generate = async (e) => {
    e.preventDefault();
    setGenerating(true);
    try {
      const draft = await adminGenerateBlog(topic);
      setEditing({ ...EMPTY, ...draft, tags: (draft.tags || []).join(', '), status: 'draft' });
      setGenOpen(false); setTopic('');
      toast({ title: 'Draft generated', description: 'Review, tweak and publish when ready.' });
    } catch (err) {
      toast({ title: 'Generation failed', description: err?.response?.data?.detail || 'Please try again.' });
    } finally { setGenerating(false); }
  };

  const load = () => adminListBlog().then(setPosts).catch(() => setPosts([]));
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({ ...EMPTY });
  const openEdit = async (p) => {
    const { data } = await axios.get(`${API_BASE}/admin/blog/${p.id}`, { headers: { 'X-Admin-Token': localStorage.getItem('hipro_admin_token') || '' } });
    setEditing({ ...data, tags: (data.tags || []).join(', ') });
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      title: editing.title, excerpt: editing.excerpt, content: editing.content,
      cover_image: editing.cover_image, author: editing.author, status: editing.status,
      tags: String(editing.tags || '').split(',').map(t => t.trim()).filter(Boolean),
    };
    try {
      if (editing.id) await adminUpdateBlog(editing.id, payload);
      else await adminCreateBlog(payload);
      toast({ title: payload.status === 'draft' ? 'Draft saved' : (editing.id ? 'Post updated' : 'Post published') });
      setEditing(null); load();
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail?.toString?.() || '' });
    } finally { setBusy(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try { await adminDeleteBlog(p.id); setPosts(cur => cur.filter(x => x.id !== p.id)); toast({ title: 'Post deleted' }); }
    catch { toast({ title: 'Delete failed' }); }
  };

  if (editing) return (
    <form onSubmit={save} className="rounded-2xl border bg-white p-6 space-y-4" data-testid="blog-editor-form">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-extrabold">{editing.id ? 'Edit article' : 'New article'}</h2>
        <Button type="button" variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
      </div>
      <div><Label>Title *</Label><Input data-testid="blog-title-input" value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })} required /></div>
      <div><Label>Excerpt (shown in list + meta description)</Label><Textarea rows={2} value={editing.excerpt} onChange={e => setEditing({ ...editing, excerpt: e.target.value })} /></div>
      <div><Label>Content (Markdown: ## headings, **bold**, - lists)</Label><Textarea data-testid="blog-content-input" rows={16} className="font-mono text-xs" value={editing.content} onChange={e => setEditing({ ...editing, content: e.target.value })} required /></div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div><Label>Cover image URL</Label><Input value={editing.cover_image} onChange={e => setEditing({ ...editing, cover_image: e.target.value })} placeholder="https://…" /></div>
        <div><Label>Tags (comma separated)</Label><Input value={editing.tags} onChange={e => setEditing({ ...editing, tags: e.target.value })} placeholder="AI, Guides" /></div>
        <div><Label>Author</Label><Input value={editing.author} onChange={e => setEditing({ ...editing, author: e.target.value })} /></div>
        <div>
          <Label>Status</Label>
          <select value={editing.status} onChange={e => setEditing({ ...editing, status: e.target.value })} className="mt-1 w-full h-10 rounded-md border px-3 text-sm bg-white">
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>
        </div>
      </div>
      <Button data-testid="blog-save-btn" type="submit" disabled={busy} className="bg-orange-500 hover:bg-orange-600">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing.status === 'draft' ? 'Save draft' : (editing.id ? 'Save changes' : 'Publish article'))}
      </Button>
    </form>
  );

  return (
    <div className="rounded-2xl border bg-white overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b">
        <div className="font-bold">Articles {posts ? `(${posts.length})` : ''}</div>
        <div className="flex gap-2">
          <Button data-testid="blog-ai-draft-btn" onClick={() => setGenOpen(v => !v)} variant="outline" className="h-9 border-orange-300 text-orange-600 hover:bg-orange-50">
            <Sparkles className="h-4 w-4 mr-1" /> AI Draft
          </Button>
          <Button data-testid="blog-new-btn" onClick={openNew} className="bg-orange-500 hover:bg-orange-600 h-9"><Plus className="h-4 w-4 mr-1" /> New article</Button>
        </div>
      </div>
      {genOpen && (
        <form onSubmit={generate} className="px-5 py-4 border-b bg-orange-50/50 flex flex-col sm:flex-row gap-2" data-testid="ai-draft-form">
          <Input
            data-testid="ai-draft-topic-input"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="Topic idea, e.g. 'Why every retailer needs an AI chatbot in 2026'"
            required
            minLength={3}
            className="flex-1 bg-white"
          />
          <Button data-testid="ai-draft-generate-btn" type="submit" disabled={generating} className="bg-orange-500 hover:bg-orange-600">
            {generating ? (<><Loader2 className="h-4 w-4 animate-spin mr-1.5" /> Writing…</>) : (<><Sparkles className="h-4 w-4 mr-1.5" /> Generate draft</>)}
          </Button>
        </form>
      )}
      {posts === null ? (
        <div className="py-16 text-center text-slate-500"><Loader2 className="h-5 w-5 mx-auto animate-spin" /></div>
      ) : posts.length === 0 ? (
        <div className="py-16 text-center text-slate-500">No articles yet.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-700">
            <tr><th className="text-left px-4 py-2.5">Title</th><th className="text-left px-4 py-2.5">Status</th><th className="text-left px-4 py-2.5">Published</th><th className="px-4 py-2.5 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {posts.map(p => (
              <tr key={p.id} className="border-t" data-testid={`blog-row-${p.slug}`}>
                <td className="px-4 py-3 font-semibold">{p.title}<div className="text-xs text-slate-400 font-mono font-normal">/blog/{p.slug}</div></td>
                <td className="px-4 py-3"><span className={`text-xs font-semibold rounded-full px-2 py-0.5 ${p.status === 'published' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-600 border'}`}>{p.status}</span></td>
                <td className="px-4 py-3 text-slate-500">{p.published_at ? new Date(p.published_at).toLocaleDateString() : '—'}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="inline-flex p-2 text-slate-500 hover:text-slate-900" title="View"><Eye className="h-4 w-4" /></a>
                  <button onClick={() => openEdit(p)} className="inline-flex p-2 text-slate-500 hover:text-orange-600" title="Edit" data-testid={`blog-edit-${p.slug}`}><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(p)} className="inline-flex p-2 text-slate-500 hover:text-rose-600" title="Delete"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
